import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ShieldCheck,
  Cpu,
  Server,
  Network,
  Code2,
  Briefcase,
  Headphones,
  GraduationCap,
  Award,
  Network as OrgIcon,
} from "lucide-react";
import { director, team } from "@/data/team";
import { Footer } from "@/components/Footer";

const ICONS = { ShieldCheck, Cpu, Server, Network, Code2, Briefcase, Headphones };

const TeamHeader = () => (
  <header className="border-b border-white/10 bg-[#05070f]/80 backdrop-blur-xl">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-8">
      <Link to="/" className="flex items-center gap-2.5" data-testid="team-home-logo">
        <img src="/cyvr-logo.png" alt="CYVR" className="h-9 w-9 object-contain drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]" />
        <span className="font-head text-sm font-bold tracking-tight text-white">
          CYVR<span className="text-cyber-cyan"> LABS</span>
        </span>
      </Link>
      <div className="flex items-center gap-5">
        <Link to="/organigrama" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-white/60 transition-colors hover:text-cyber-cyan" data-testid="team-to-org">
          <OrgIcon className="h-4 w-4" /> Organigrama
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-white/60 transition-colors hover:text-cyber-cyan">
          <ArrowLeft className="h-4 w-4" /> Inicio
        </Link>
      </div>
    </div>
  </header>
);

const MemberCard = ({ m, index, featured }) => {
  const Icon = ICONS[m.icon] || Cpu;
  return (
    <motion.article
      data-testid={`member-${m.id}`}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative overflow-hidden rounded-2xl border bg-white/[0.02] transition-colors duration-300 hover:border-cyber-cyan/40 ${
        featured ? "border-cyber-cyan/30" : "border-white/10"
      }`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={m.photo}
          alt={m.name}
          loading="lazy"
          className="h-full w-full object-cover object-top grayscale contrast-[1.08] brightness-95 transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
        />
        <div className="pointer-events-none absolute inset-0 bg-cyber-cyan/25 mix-blend-color transition-opacity duration-500 group-hover:opacity-0" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-cyber-cyan/10 to-cyber-violet/20 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070f] via-[#05070f]/30 to-transparent" />
        <div className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-cyber-cyan/40 bg-[#05070f]/70 text-cyber-cyan backdrop-blur">
          <Icon className="h-4.5 w-4.5" />
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-cyber-cyan">{m.area}</p>
          <h3 className="mt-1 font-head text-xl font-bold text-white">{m.name}</h3>
          <p className="font-mono-tech text-[11px] uppercase tracking-widest text-white/60">{m.role}</p>
        </div>
      </div>

      <div className="p-5">
        <p className="font-sans text-sm leading-relaxed text-white/70">{m.summary}</p>

        <div className="mt-5">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50">
            <GraduationCap className="h-3.5 w-3.5 text-cyber-cyan" /> Currículum
          </p>
          <ul className="mt-2 space-y-1.5">
            {m.cv.map((c, i) => (
              <li key={i} className="flex gap-2 font-sans text-xs leading-relaxed text-white/70">
                <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-cyber-cyan/80" />
                {c}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-5">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50">
            <Award className="h-3.5 w-3.5 text-cyber-violet" /> Trabajos realizados
          </p>
          <ul className="mt-2 space-y-1.5">
            {m.refs.map((r, i) => (
              <li key={i} className="flex gap-2 font-sans text-xs leading-relaxed text-white/70">
                <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-cyber-violet/80" />
                {r}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.article>
  );
};

const DirectorCard = ({ m }) => {
  const Icon = ICONS[m.icon] || ShieldCheck;
  return (
    <motion.section
      data-testid={`member-${m.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="group mt-14 overflow-hidden rounded-2xl border border-cyber-cyan/30 bg-white/[0.02] shadow-[0_0_36px_rgba(0,240,255,0.1)]"
    >
      <div className="grid md:grid-cols-[300px_1fr]">
        <div className="relative min-h-[280px] overflow-hidden md:min-h-full">
          <img
            src={m.photo}
            alt={m.name}
            className="absolute inset-0 h-full w-full object-cover object-top grayscale contrast-[1.08] brightness-95 transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
          />
          <div className="pointer-events-none absolute inset-0 bg-cyber-cyan/25 mix-blend-color transition-opacity duration-500 group-hover:opacity-0" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-cyber-cyan/10 to-cyber-violet/20 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-0" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#05070f]/70 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#05070f]/60" />
          <div className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-cyber-cyan/40 bg-[#05070f]/70 text-cyber-cyan backdrop-blur">
            <Icon className="h-5 w-5" />
          </div>
        </div>

        <div className="p-6 md:p-8">
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-cyber-cyan">{m.area}</p>
          <h2 className="mt-2 font-head text-3xl font-bold text-white">{m.name}</h2>
          <p className="mt-1 font-mono-tech text-xs uppercase tracking-widest text-white/60">{m.role}</p>
          <p className="mt-4 max-w-2xl font-sans text-sm leading-relaxed text-white/70">{m.summary}</p>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <p className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50">
                <GraduationCap className="h-3.5 w-3.5 text-cyber-cyan" /> Currículum
              </p>
              <ul className="mt-2 space-y-1.5">
                {m.cv.map((c, i) => (
                  <li key={i} className="flex gap-2 font-sans text-xs leading-relaxed text-white/70">
                    <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-cyber-cyan/80" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50">
                <Award className="h-3.5 w-3.5 text-cyber-violet" /> Trabajos realizados
              </p>
              <ul className="mt-2 space-y-1.5">
                {m.refs.map((r, i) => (
                  <li key={i} className="flex gap-2 font-sans text-xs leading-relaxed text-white/70">
                    <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-cyber-violet/80" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default function Team() {
  return (
    <div className="relative z-10 min-h-screen" data-testid="page-equipo">
      <TeamHeader />
      <main className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.28em] text-cyber-cyan">Nuestro equipo</p>
        <h1 className="mt-4 max-w-3xl font-head text-4xl font-bold tracking-tight text-white sm:text-5xl">
          El equipo que defiende tu empresa
        </h1>
        <p className="mt-5 max-w-2xl font-sans text-base leading-relaxed text-white/60">
          Un equipo multidisciplinar de ingeniería, sistemas, redes, desarrollo y operaciones, dirigido por
          el fundador de CYVR Labs.
        </p>

        <DirectorCard m={director} />

        <section className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {team.map((m, i) => (
            <MemberCard key={m.id} m={m} index={i} />
          ))}
        </section>

        <div className="mt-16 flex flex-wrap items-center gap-4">
          <Link
            to="/organigrama"
            data-testid="team-view-org-btn"
            className="inline-flex items-center gap-2 rounded-full bg-cyber-cyan px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#05070f] transition-transform hover:scale-[1.03]"
          >
            <OrgIcon className="h-4 w-4" /> Ver organigrama
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest text-white/70 transition-colors hover:border-cyber-cyan hover:text-cyber-cyan"
          >
            Volver al inicio
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
